import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { CHART_CONFIG } from '~/components';
import { steps, useFormDataContext } from '~/hooks';
import { ChartData, ExploreYourOptionsIncome, ExploreYourOptionsPot, FormKeys, IncomeType } from '~/types';
import { DEFAULT_DRAWDOWN_END_AGE, formatToGBP, getAgeRange, STATE_PENSION } from '~/utils';
import { ChartContext } from './ChartContext';

type IncomeSource = {
    label: string;
    value: number;
};

const incomeTypes = {
    statePension: 'State pension',
    finalSalary: 'Final salary',
    otherIncome: 'Other income',
    annuityIncomePerYear: 'Annuity',
    drawdownIncomePerYear: 'Drawdown',
};

export const ChartProvider = ({ children }: { children: React.ReactNode }) => {
    const formData = useFormDataContext();
    const { pathname } = useLocation();

    const isExploring = pathname === '/explore';

    const { defaultPots, explorePots } = formData?.pots || {};
    const { amount: targetIncome } = formData?.targetIncome || {};
    const { personalInformation } = formData?.customerDetails || {};

    const defaultPot: ExploreYourOptionsPot = {
        startAge: personalInformation?.retirementAge,
        endAge: DEFAULT_DRAWDOWN_END_AGE,
        incomes: [
            {
                type: IncomeType.STATE_PENSION,
                amount: STATE_PENSION,
            },
        ],
        totalIncome: STATE_PENSION,
    };

    const currentPots =
        isExploring && explorePots?.length > 0 ? explorePots : defaultPots?.length > 0 ? defaultPots : [defaultPot];

    const targetStatuses = currentPots.map(pot => {
        const incomeBeforeAnnuity = pot.incomes
            .filter(income => income.type !== IncomeType.ANNUITY && income.type !== IncomeType.DRAWDOWN)
            .reduce((acc, item) => acc + Number(item.amount), 0);
        const potTotal = pot.incomes.reduce((acc, item) => acc + Number(item.amount), 0);

        if (incomeBeforeAnnuity >= targetIncome) {
            return 'TARGET_EXCEEDED';
        } else if (potTotal >= targetIncome) {
            return 'TARGET_REACHED';
        } else {
            return 'TARGET_NOT_REACHED';
        }
    });

    const incomePots = currentPots;
    const unmetTargetPots = useMemo(() => {
        const pots: { startAge: number; endAge: number | null }[] = [];

        targetStatuses.forEach((status, index) => {
            if (status === 'TARGET_NOT_REACHED') {
                const { startAge, endAge } = incomePots[index];
                pots.push({ startAge, endAge });
            }
        });

        return pots;
    }, [incomePots, targetStatuses]);

    const getIncomeStatusContent = () => {
        const formatRanges = (ranges: { startAge: number; endAge: number | null }[]) => {
            const formatted = ranges.map(({ startAge, endAge }) => getAgeRange(startAge, endAge));
            if (formatted.length === 1) return formatted[0];
            if (formatted.length === 2) return `${formatted[0]} and ${formatted[1]}`;
            return `${formatted.slice(0, -1).join(', ')} and ${formatted[formatted.length - 1]}`;
        };

        if (!unmetTargetPots.length) {
            return `You could meet your income target throughout retirement with these options`;
        }

        if (unmetTargetPots.length === incomePots.length) {
            return `It looks like you can't meet your income target throughout retirement with these options`;
        }

        return `It looks like you can't meet your income target from ${formatRanges(unmetTargetPots)} with these options`;
    };

    const getIncomeStatusForAge = () => {
        const statuses: string[] = [];
        incomePots.map((pot, index) => {
            const potTotalIncome = data[index].reduce((acc, item) => acc + Number(item.y), 0);
            const potTargetMet = potTotalIncome >= targetIncome;
            const ageRangeText = `Age ${getAgeRange(pot.startAge, pot.endAge)}`;

            statuses.push(
                `Popup display - You ${potTargetMet ? '' : "don't "}meet the income target - ${ageRangeText} (${formatToGBP(potTotalIncome)})`
            );
        });
        return statuses.join(' | ');
    };

    const data = useMemo(() => {
        const createChartData = (sources: IncomeSource[]): ChartData[] => {
            const currentIndex = steps.indexOf(pathname);
            const futurePaths = steps.slice(currentIndex + 1);
            const visibleConfigs = CHART_CONFIG.filter(config => !futurePaths.includes(config.route));

            return sources
                .filter(source => source.value > 0)
                .map(source => {
                    const config = visibleConfigs.find(c => c.label === source.label);
                    if (!config) return null;

                    return {
                        label: source.label,
                        y: source.value,
                        color: config.color,
                    };
                })
                .filter(Boolean) as ChartData[];
        };

        const incomeSources: IncomeSource[][] = [];
        currentPots.map(pot => incomeSources.push(getIncomeSources(pot)));

        const chartData: ChartData[][] = [];
        incomeSources.map(source => chartData.push(createChartData(source)));

        return chartData;
    }, [formData, isExploring, pathname, currentPots]);

    const shortfall = useMemo(() => {
        const incomeBeforeAnnuity = currentPots[0].incomes
            .filter(income => income.type !== IncomeType.ANNUITY && income.type !== IncomeType.DRAWDOWN)
            .reduce((acc, item) => acc + Number(item.amount), 0);
        return Math.max(0, targetIncome - incomeBeforeAnnuity);
    }, [targetIncome, currentPots]);

    const updateDefaultPots = ({ type, amount }: ExploreYourOptionsIncome) => {
        const pots = formData.pots;
        const defaultPots = pots?.defaultPots || [];

        if (!defaultPots?.length) {
            formData.updateFormDataState({
                [FormKeys.POTS]: {
                    ...pots,
                    defaultPots: [defaultPot],
                },
            });
        } else {
            const updatedPots = defaultPots.map(pot => {
                const incomeIndex = pot.incomes.findIndex(income => income.type === type);
                if (incomeIndex !== -1) {
                    pot.incomes[incomeIndex].amount = amount;
                } else {
                    pot.incomes.push({ type, amount });
                }
                return { ...pot, startAge: personalInformation.retirementAge, endAge: DEFAULT_DRAWDOWN_END_AGE };
            });

            formData.updateFormDataState({ [FormKeys.POTS]: { ...pots, defaultPots: updatedPots } });
        }
    };

    const updateExplorePots = (newPots: ExploreYourOptionsPot[]) => {
        const pots = formData.pots;
        formData.updateFormDataState({ [FormKeys.POTS]: { ...pots, explorePots: newPots } });
    };

    const contextValue = {
        data,
        shortfall,
        targetStatuses,
        getIncomeStatusContent,
        getIncomeStatusForAge,
        unmetTargetPots,
        updateDefaultPots,
        updatePots: updateExplorePots,
    };

    return <ChartContext.Provider value={contextValue}>{children}</ChartContext.Provider>;
};

const getIncomeSources = (currentPot: ExploreYourOptionsPot | null): IncomeSource[] => {
    return Object.entries(incomeTypes).map(([type, label]) => ({
        label,
        value: currentPot?.incomes?.find(income => income.type === type)?.amount || 0,
    }));
};
